import React, { useState, useEffect, useRef } from 'react';
import { 
  Download, 
  Upload, 
  Trash2, 
  Database, 
  RefreshCw, 
  CheckCircle,
  AlertTriangle,
  Loader,
  Archive
} from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const DatabaseManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState({});
  const [backupFile, setBackupFile] = useState(null);
  const [integrityResult, setIntegrityResult] = useState(null);
  const [exportProgress, setExportProgress] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const eventSourceRef = useRef(null);

  const setOperationLoading = (operation, isLoading) => {
    setLoading(prev => ({ ...prev, [operation]: isLoading }));
  };

  // Listen to SSE progress updates
  const listenProgress = (taskId, setProgress, onComplete) => {
    const token = localStorage.getItem('token');
    const baseURL = (window.ENV && window.ENV.REACT_APP_API_URL) || 'http://localhost:5000';
    
    // Use polling since EventSource doesn't support custom headers
    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`/api/database/progress/${taskId}`);
        // SSE endpoint — we'll use polling instead
      } catch (e) {}
    }, 1000);

    // Actually use a simpler polling approach
    clearInterval(poll);
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(`${baseURL}/api/database/progress-poll/${taskId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProgress(data);
          if (data.percent >= 100 || data.step === 'error') {
            clearInterval(pollInterval);
            if (onComplete) onComplete(data);
          }
        }
      } catch (e) {
        // Polling failed — ignore
      }
    }, 800);
    
    return () => clearInterval(pollInterval);
  };

  const exportBackup = async () => {
    setOperationLoading('export', true);
    setExportProgress({ step: 'init', percent: 0, message: 'Starting backup...' });
    
    try {
      // Start export and get taskId
      const startRes = await axios.post('/api/database/export/start');
      const { taskId } = startRes.data;
      
      // Poll for progress
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`/api/database/progress-poll/${taskId}`);
          setExportProgress(res.data);
          
          if (res.data.step === 'ready') {
            clearInterval(pollInterval);
            // Download the file
            const downloadRes = await axios.get(`/api/database/export/download/${taskId}`, {
              responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `agms_backup_${new Date().toISOString().slice(0, 10)}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setExportProgress({ step: 'done', percent: 100, message: 'Backup downloaded!' });
            toast.success('Backup exported successfully (Database + Uploads)');
            setTimeout(() => setExportProgress(null), 3000);
            setOperationLoading('export', false);
          } else if (res.data.step === 'error') {
            clearInterval(pollInterval);
            toast.error(res.data.message);
            setTimeout(() => setExportProgress(null), 5000);
            setOperationLoading('export', false);
          }
        } catch (e) {
          // Keep polling
        }
      }, 800);
      
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start backup');
      setExportProgress(null);
      setOperationLoading('export', false);
    }
  };

  const importBackup = async () => {
    if (!backupFile) {
      toast.error('Please select a backup file');
      return;
    }

    if (!window.confirm('⚠️ This will overwrite ALL existing data and uploaded files. Are you sure?')) {
      return;
    }

    setOperationLoading('import', true);
    setImportProgress({ step: 'uploading', percent: 5, message: 'Uploading file...' });
    
    try {
      const formData = new FormData();
      formData.append('backup_file', backupFile);
      
      const response = await axios.post('/api/database/import', formData, {
        timeout: 0,
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded / progressEvent.total) * 30);
          setImportProgress({ step: 'uploading', percent: pct, message: `Uploading... ${pct}%` });
        }
      });
      
      // If taskId returned, poll for progress
      if (response.data.taskId) {
        const { taskId } = response.data;
        const pollInterval = setInterval(async () => {
          try {
            const res = await axios.get(`/api/database/progress-poll/${taskId}`);
            setImportProgress(res.data);
            if (res.data.percent >= 100 || res.data.step === 'error' || res.data.step === 'done') {
              clearInterval(pollInterval);
            }
          } catch (e) {}
        }, 800);
      }
      
      setImportProgress({ step: 'done', percent: 100, message: response.data.message });
      toast.success(response.data.message);
      setBackupFile(null);
      setTimeout(() => setImportProgress(null), 5000);
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to import backup';
      setImportProgress({ step: 'error', percent: 0, message: msg });
      toast.error(msg);
      setTimeout(() => setImportProgress(null), 5000);
    } finally {
      setOperationLoading('import', false);
    }
  };

  const deleteLogs = async () => {
    if (!window.confirm('Are you sure you want to delete all logs?')) return;
    setOperationLoading('deleteLogs', true);
    try {
      const response = await axios.post('/api/database/delete-logs');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete logs');
    } finally {
      setOperationLoading('deleteLogs', false);
    }
  };

  const compactDatabase = async () => {
    setOperationLoading('compact', true);
    try {
      const response = await axios.post('/api/database/compact');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to compact database');
    } finally {
      setOperationLoading('compact', false);
    }
  };

  const reindexDatabase = async () => {
    setOperationLoading('reindex', true);
    try {
      const response = await axios.post('/api/database/reindex');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reindex database');
    } finally {
      setOperationLoading('reindex', false);
    }
  };

  const runIntegrityCheck = async () => {
    setOperationLoading('integrity', true);
    try {
      const response = await axios.get('/api/database/integrity-check');
      setIntegrityResult(response.data);
      toast.success('Integrity check completed');
    } catch (error) {
      setIntegrityResult({ status: 'error', message: error.response?.data?.error || 'Check failed' });
      toast.error('Integrity check failed');
    } finally {
      setOperationLoading('integrity', false);
    }
  };

  const ProgressBar = ({ progress }) => {
    if (!progress) return null;
    const isError = progress.step === 'error';
    const isDone = progress.step === 'done' || progress.step === 'ready';
    
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm font-medium ${isError ? 'text-red-700' : isDone ? 'text-green-700' : 'text-blue-700'}`}>
            {progress.message}
          </span>
          <span className="text-sm text-gray-500">{progress.percent}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full transition-all duration-300 ${
              isError ? 'bg-red-600' : isDone ? 'bg-green-600' : 'bg-blue-600'
            }`}
            style={{ width: `${Math.max(progress.percent, 2)}%` }}
          />
        </div>
      </div>
    );
  };

  const ActionButton = ({ onClick, loading: isLoading, icon: Icon, children, variant = 'primary', disabled = false }) => {
    const baseClasses = "flex items-center px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
    const variants = {
      primary: "bg-blue-600 text-white hover:bg-blue-700",
      danger: "bg-red-600 text-white hover:bg-red-700",
      secondary: "bg-gray-600 text-white hover:bg-gray-700",
      success: "bg-green-600 text-white hover:bg-green-700"
    };

    return (
      <button onClick={onClick} disabled={isLoading || disabled} className={`${baseClasses} ${variants[variant]}`}>
        {isLoading ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : <Icon className="w-4 h-4 mr-2" />}
        {children}
      </button>
    );
  };

  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="max-w-4xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Database Management</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-3" />
            <div>
              <h3 className="font-medium text-red-800">Access Denied</h3>
              <p className="text-red-600 mt-1">Database management requires administrator privileges.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Database Management</h2>

      <div className="space-y-6">
        {/* Backup & Restore */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Backup & Restore
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Export */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Archive className="w-4 h-4" /> Export Full Backup
              </h4>
              <p className="text-sm text-gray-600">
                Downloads a .zip containing the complete database (SQL) and all uploaded files (photos, documents).
              </p>
              <ActionButton
                onClick={exportBackup}
                loading={loading.export}
                icon={Download}
                variant="success"
              >
                {loading.export ? 'Exporting...' : 'Export Backup (.zip)'}
              </ActionButton>
              <ProgressBar progress={exportProgress} />
            </div>

            {/* Import */}
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Upload className="w-4 h-4" /> Import Backup
              </h4>
              <p className="text-sm text-gray-600">
                Restore from a .zip (full), .sql (database only), or .json (legacy) backup file.
              </p>
              <input
                type="file"
                accept=".zip,.sql,.json"
                onChange={(e) => setBackupFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {backupFile && (
                <p className="text-xs text-gray-500">
                  Selected: {backupFile.name} ({(backupFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              <ActionButton
                onClick={importBackup}
                loading={loading.import}
                icon={Upload}
                disabled={!backupFile}
                variant="secondary"
              >
                {loading.import ? 'Importing...' : 'Import Backup'}
              </ActionButton>
              <ProgressBar progress={importProgress} />
            </div>
          </div>
        </div>

        {/* Database Operations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Database Operations</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ActionButton onClick={deleteLogs} loading={loading.deleteLogs} icon={Trash2} variant="secondary">
              Delete Logs
            </ActionButton>
            <ActionButton onClick={compactDatabase} loading={loading.compact} icon={Database} variant="secondary">
              Compact Database
            </ActionButton>
            <ActionButton onClick={reindexDatabase} loading={loading.reindex} icon={RefreshCw} variant="secondary">
              Reindex Database
            </ActionButton>
          </div>
        </div>

        {/* Integrity Check */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Database Health</h3>
          <ActionButton onClick={runIntegrityCheck} loading={loading.integrity} icon={CheckCircle}>
            Run Integrity Check
          </ActionButton>

          {integrityResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              integrityResult.status === 'healthy' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start">
                {integrityResult.status === 'healthy' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
                )}
                <div className="flex-1">
                  <h4 className={`font-medium ${integrityResult.status === 'healthy' ? 'text-green-800' : 'text-red-800'}`}>
                    {integrityResult.status === 'healthy' ? 'Database Healthy' : 'Issues Found'}
                  </h4>
                  {integrityResult.summary && (
                    <div className="text-sm text-gray-600 mt-2 space-y-1">
                      <p>Tables: {integrityResult.summary.totalTables}</p>
                      <p>Size: {integrityResult.summary.databaseSize}</p>
                    </div>
                  )}
                  {integrityResult.tableStats && integrityResult.tableStats.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pr-4 py-1">Table</th>
                            <th className="pr-4 py-1">Rows</th>
                            <th className="py-1">Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {integrityResult.tableStats.map((t, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="pr-4 py-1 font-mono">{t.table}</td>
                              <td className="pr-4 py-1">{t.rows}</td>
                              <td className="py-1">{t.size}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DatabaseManagement;
